// components/MathInput.tsx
'use client' // 如果在 Next.js 中使用

import 'mathlive'
import React from 'react'
import { useEffect, useRef } from 'react'
import './styles.css'
import { VirtualKeyboardKeycap, VirtualKeyboardLayout } from 'mathlive'
import { normalizeMathFieldLatex } from '@/components/Math/math-input-normalize'

type MathInputProps = {
  value: string
  placeholder?: string
  readOnly?: boolean
  smartSuperscript?: boolean
  autoFocus?: boolean
  onEnterKey?: () => void
  onChange: (val: string) => void
  icon?: React.ReactNode
  onIconClick?: () => void
}

function mathLayout(): VirtualKeyboardLayout {
  return {
    label: 'math',
    labelClass: '',
    rows: [
      [
        "\\leq",
        "\\geq",
        "<",
        ">",
        '[separator-5]',
        {label: '[7]', width: 1.0, shift: {label: '7'}},
        {label: '[8]', width: 1.0, shift: {label: '8'}},
        {label: '[9]', width: 1.0, shift: {label: '9'}},
        {label: '[/]', width: 1.0, shift: {label: '/'}},
        '[separator-5]',
        {label: '[backspace]', width: 2.0},
        {label: '[hide-keyboard]', width: 1.0},
      ],
      [
        "\\neq",
        "\\pm",
        "\\pi",
        "\\%",
        '[separator-5]',
        {label: '[4]', width: 1.0, shift: {label: '4'}},
        {label: '[5]', width: 1.0, shift: {label: '5'}},
        {label: '[6]', width: 1.0, shift: {label: '6'}},
        '[*]',
        '[separator-5]',
        '\\frac{#0}{#?}',
        '#0 \\frac{#0}{#?}',
        {latex: '{#0}', command: ['moveToMathfieldEnd']},
      ],
      [
        "(",
        ")",
        "\\{",
        "\\}",
        '[separator-5]',
        {label: '[1]', width: 1.0, shift: {label: '1'}},
        {label: '[2]', width: 1.0, shift: {label: '2'}},
        {label: '[3]', width: 1.0, shift: {label: '3'}},
        {label: '[-]', width: 1.0, shift: {label: '-'}},
        '[separator-5]',
        '{#0}^{#?}',
        '{#0}_{#?}',
        '{#0}_{#?}^{#0}',
      ],
      [
        "\\sqrt{#0}",
        "\\sqrt[3]{#0}",
        "\\sqrt[#?]{#0}",
        "\\left| #? \\right|",
        '[separator-5]',
        {label: '[0]', width: 1.0, shift: {label: '0'}},
        {label: '[.]', width: 1.0, shift: {label: '.'}},
        {label: '[=]', width: 1.0, shift: {label: '='}},
        {label: '[+]', width: 1.0, shift: {label: '+'}},
        '[separator-5]',
        {label: '[left]', width: 1.5},
        {label: '[right]', width: 1.5},
      ],    
    ]
  }
}

function alphabeticLayout(): VirtualKeyboardLayout {
  const rows: (string | Partial<VirtualKeyboardKeycap>)[][] = [];
  const template = ['qwertyuiop', ' asdfghjkl ', '^zxcvbnm~']

  for (const templateRow of template) {
    const row: (string | Partial<VirtualKeyboardKeycap>)[] = [];
    for (const k of templateRow) {
      if (/[a-z]/.test(k)) {
        row.push({
          label: k,
          class: 'hide-shift',
        });
      } else if (k === '~') {
        row.push({ label: '[backspace]', width: 1.0 });
      } else if (k === '^') row.push('[shift]');
      else if (k === ' ') row.push('[separator-5]');
    }
    rows.push(row);
  }

  rows.push([
    '[-]',
    '[+]',
    '[=]',
    { label: ' ', width: 1.5 },
    { label: ',', shift: ';', variants: '.', class: 'hide-shift' },
    '[.]',
    '[left]',
    '[right]',
    { label: '[action]', width: 1.5 },
  ]);

  return {
    label: 'abc',
    labelClass: 'MLK__tex-math',
    tooltip: 'keyboard.tooltip.alphabetic',
    rows: rows,
  };
}

function symbolsLayout(): VirtualKeyboardLayout {
  return {
    label: '&infin;≠∈',
    labelClass: 'MLK__tex',
    tooltip: 'keyboard.tooltip.symbols',
    rows: [
      [
        {
          latex: '\\sin',
        },
        '\\ln',
        {
          latex: '\\mathrm{abs}',
          insert: '\\mathrm{abs}\\left(#0\\right)',
        },
        {
          latex: '\\rightarrow',
          shift: '\\Rightarrow',
          variants: [
            { latex: '\\implies', aside: 'implies' },

            { latex: '\\to', aside: 'to' },
            '\\dashv',
            { latex: '\\roundimplies', aside: 'round implies' },
          ],
        },
        {
          latex: '\\exists',
        },
        { latex: '\\in' },
        '\\cup',
        {
          latex: '\\overrightarrow{#@}',
        },
        {
          class: 'small hide-shift',
          latex: '\\lim_{#?}',
        },
        '\\exponentialE',
      ],
      [
        {
          latex: '\\cos',
        },
        {
          latex: '\\log',
        },
        '\\left\\vert#0\\right\\vert',
        {
          latex: '\\larr',
        },
        {
          latex: '\\forall',
        },
        { latex: '\\ni' },
        '\\cap',
        {
          latex: '\\overline{#@}',
        },
        {
          class: 'hide-shift small',
          latex: '\\int',
        },
        { latex: '\\pi' },
      ],
      [
        {
          latex: '\\tan',
        },
        {
          latex: '\\exp',
        },
        '\\left\\Vert#0\\right\\Vert',
        {
          latex: '\\lrArr',
        },
        { latex: '\\vert', shift: '!' },
        {
          latex: '#@^{\\complement}',
        },
        {
          latex: '\\subset',
        },
        {
          latex: '#@^{\\prime}',
        },
        {
          latex: '\\mathrm{d}',
        },
        {
          latex: '\\infty',
        },
      ],
      [
        { label: '[shift]', width: 2.0 },
        {
          class: 'box',
          latex: ',',
          shift: ';',
          variants: [';', '?'],
        },
        {
          class: 'box',
          latex: '\\colon',
        },
        {
          class: 'box',
          latex: '\\cdot',
        },
        '[separator]',
        '[left]',
        '[right]',
        {
          label: '[backspace]',
          width: 1.0,
          class: 'action hide-shift',
        },
        { label: '[action]', width: 1.0 },
      ],
    ],
  };
}

export default function MathInput({
  value,
  onChange,
  placeholder,
  readOnly = false,
  onEnterKey,
  smartSuperscript = false,
  autoFocus = true,
  icon,
  onIconClick,
}: MathInputProps) {
  const fieldRef = useRef<any>(null)
  const keyboardInitialized = useRef(false)

  // 初始化键盘配置
  useEffect(() => {
    if (!keyboardInitialized.current) {
      window.mathVirtualKeyboard.layouts = [mathLayout(), alphabeticLayout(), symbolsLayout()];
      keyboardInitialized.current = true;
    }
  }, []);

  useEffect(() => {
    const field = fieldRef.current
    if (!field) return

    const handleInput = () => {
      // Use expanded LaTeX to reduce shorthand/ambiguous output from mathlive
      const rawLatex = field.getValue('latex-expanded')
      const latex = normalizeMathFieldLatex(rawLatex)
      onChange(latex)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !readOnly) {
        event.preventDefault()
        if (onEnterKey) {
          onEnterKey()
        }
        // Re-focus after a short delay to ensure the field remains focused
        setTimeout(() => {
          field.focus()
        }, 100)
      }
    }

    const handleKeyboard = () => {
      // 键盘配置已在组件挂载时完成，这里只需显示键盘
      // window.mathVirtualKeyboard.visible = true;
    }
    

    field.virtualKeyboardMode = 'onfocus' // 'manual', 'onfocus', 'off'
    field.virtualKeyboards = 'all'
    field.smartFence = true
    field.smartMode = true
    field.readOnly = readOnly

    field.setValue(value)
    field.addEventListener('input', handleInput)
    field.addEventListener('keydown', handleKeyDown)
    field.addEventListener('focus', handleKeyboard)

    // Auto focus when component mounts or when readOnly changes to false
    if (!readOnly && autoFocus) {
      setTimeout(() => {
        field.focus()
      }, 100)
    }

    return () => {
      field.removeEventListener('input', handleInput)
      field.removeEventListener('keydown', handleKeyDown)
      field.removeEventListener('focus', handleKeyboard)
    }
  }, [onChange, value, readOnly, onEnterKey, autoFocus])

  // Add global Enter key listener to refocus math input
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      const field = fieldRef.current
      if (event.key === 'Enter' && field && !readOnly) {
        // Check if the math field is not already focused
        if (document.activeElement !== field) {
          setTimeout(() => {
            field.focus()
          }, 100)
        }
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown)

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [readOnly])
  
  return React.createElement('math-field', {
    ref: fieldRef,
    style: {
      border: '1px solid #ccc',
      borderRadius: '6px',
      padding: '6px 10px',
      fontSize: '1.2rem',
      width: '100%',
      minHeight: '30px',
      background: readOnly ? '#f9f9f9' : 'white',
    },
    placeholder: placeholder || '',
    smartSuperscript: smartSuperscript,
  })
}
